import fb from '@/firebaseConfig';
import analytics from '@/utils/Analytics';
import { isToday } from '@/helpers/timeHelpers';

let retriesCount = 0;

/*

  STATE

 */

const initialState = () => ({
  uid: null,
  email: '',
  isMember: false,
  memberLevel: 0,
  isAnonymous: false,
  numDaysOpenedApp: null,
  marketingOptOut: false,
  welcomeSlideNum: 0, // -1 = dismissed by user
  loadRetries: 0,
  userDataLoaded: false,
});

const state = {
  ...initialState(),
};

// Consolidate user updates to save to DB during initalization
// Update isModuleDataReady with any modules that always update user Doc during init
// Not in state because doesn't need to be tracked by vuex
const userInitDataToSaveToDB = {
  isModuleDataReady: {
    dailyCards: false,
    posts: false,
    purchases: false,
    user: false,
  },
  data: {},
};

/*

  GETTERS

 */

const getters = {
  isUserSet: state => state.uid != null && state.userDataLoaded,
};

/*

  ACTIONS

 */

const actions = {
  init({ commit, dispatch }, user) {
    const currentUser = user || fb.auth().currentUser;

    // Runs once per user sign-in/app load
    if (currentUser && !state.userDataLoaded) {
      commit('setUidEmail', currentUser);
      dispatch('purchases/init', currentUser.uid, { root: true });
      dispatch('getUserData', currentUser);
    }
  },

  getUserData({ commit, dispatch, state }, currentUser) {
    const { uid } = currentUser;

    // Runs every time the user doc changes
    fb.usersCollection.doc(uid).onSnapshot(
      doc => {
        // console.log('User document successfully read', doc.data());
        // Set data from User database when data changes
        const allUserData = doc.data();
        // For new users, data is sometimes undefined at first
        if (!allUserData) {
          dispatch('handleGetUserDataError', {
            currentUser,
            error: new Error('User data not available yet'),
          });
          return;
        }
        const {
          dailyCards,
          favoriteIds,
          favoriteStackIds,
          welcomeSlideNum = 1, // For new users, this will be undefined and default to 1
          memberLevel = 0,
          lastOpenedAppAt,
          marketingOptOut,
          numDaysOpenedApp,
          posts,
          stacks,
          subscription,
        } = allUserData;

        const isMember = memberLevel > 0;
        const { userDataLoaded } = state;
        commit('setUserData', { isMember, memberLevel, marketingOptOut, numDaysOpenedApp });
        dispatch('posts/init', { posts, welcomeSlideNum }, { root: true });
        commit('dailyCards/setDailyCards', dailyCards, { root: true });
        commit('stacks/setUserStacks', stacks, { root: true });
        commit('favorites/setFavorites', favoriteIds, { root: true });
        commit('favorites/setFavoriteStacks', favoriteStackIds, { root: true });
        commit('purchases/setSubscription', subscription, { root: true });

        // Runs once per user sign-in/app load
        if (!userDataLoaded) {
          commit('setWelcomeSlideNum', welcomeSlideNum);
          dispatch('updateSignInInfo', { currentUser, lastOpenedAppAt });
          dispatch('initContentModules', { isMember });
          dispatch('purchases/checkSubscriptionStatus', memberLevel, { root: true });
          analytics.setSubscriberProperty(memberLevel);
        }
      },
      error => {
        dispatch('handleGetUserDataError', { currentUser, error });
      }
    );
  },

  handleGetUserDataError({ dispatch }, { currentUser, error }) {
    console.error('Error getting user', error);
    if (retriesCount < 31) {
      console.warn(`Trying again - ${30 - retriesCount} tries left`);
      setTimeout(() => {
        retriesCount += 1;
        dispatch('getUserData', currentUser);
      }, 300);
    } else if (!state.userDataLoaded) {
      console.error(`Couldn't load user data, no retries left`);
      analytics.exception(`Couldn't load user data, no retries left`);
      dispatch('initContentModules', { isMember: false });
    }
  },

  initContentModules({ dispatch }, { isMember }) {
    dispatch('cards/init', { isMember }, { root: true });
    dispatch('stacks/init', { isMember }, { root: true });
    dispatch('audio/init', null, { root: true });
  },

  // Saving sign-in info for troubleshooting
  // Saving num of times and days used app for displaying new user content
  async updateSignInInfo({ dispatch }, { currentUser, lastOpenedAppAt }) {
    const { email = '', emailVerified, isAnonymous } = currentUser;
    const { signInMethods = [] } = await fb.getSignInMethods(email);
    const moduleData = {
      lastOpenedAppAt: fb.currentTimestamp(),
      numTimesOpenedApp: fb.dbFieldValue.increment(1),
    };
    if (!isToday(lastOpenedAppAt ? lastOpenedAppAt.toDate() : null)) {
      moduleData.signInMethods = signInMethods;
      moduleData.isAnonymous = isAnonymous;
      moduleData.emailVerified = emailVerified;
      moduleData.numDaysOpenedApp = fb.dbFieldValue.increment(1);
    }
    // Don't trigger for localhost reloads
    // if (process.env.NODE_ENV === 'development') {
    //   console.warn('[Dev mode] Not calling user/updateSignInInfo during development');
    //   return;
    // }
    dispatch('saveUserInitDataToDB', { moduleName: 'user', moduleData });
  },

  // Consolidate user updates during vuex initialization
  // As data comes in, it gets added to userInitDataToSaveToDB
  // Once all the modules have called the function, update the DB with saved info
  // After the first batch, subsequent updates happen individually & immediately
  saveUserInitDataToDB({ state }, { moduleName, moduleData = {} }) {
    // console.log('saveUserInitDataToDB', moduleName, moduleData);
    const { uid } = state;
    const { isModuleDataReady, data } = userInitDataToSaveToDB;
    isModuleDataReady[moduleName] = true;
    Object.assign(data, moduleData);

    if (
      Object.values(isModuleDataReady).every(value => value === true) &&
      Object.keys(data).length
    ) {
      fb.usersCollection
        .doc(uid)
        .update(data)
        .catch(error => {
          console.error('Error updating user in DB during init', error);
          analytics.exception('Error updating user in DB during init');
        });
      // Reset so that any future module updates go through immediately
      userInitDataToSaveToDB.isModuleDataReady = {};
      userInitDataToSaveToDB.data = {};
    }
  },

  // Called from purchases/updateSavedSubscription
  // Reset daily cards & content modules to update content user has access to
  reinitAfterSubscriptionChange({ commit, dispatch }, isMember) {
    commit('dailyCards/setDailyCards', {}, { root: true });
    dispatch('initContentModules', { isMember });
  },

  updateWelcomeSlideNum({ state, commit }, num) {
    commit('setWelcomeSlideNum', num);
    fb.usersCollection
      .doc(state.uid)
      .update({ welcomeSlideNum: num })
      .catch(error => {
        console.error('Error updating welcomeSlideNum', error);
        analytics.exception('Error updating welcomeSlideNum');
      });
  },
};

/*

  MUTATIONS

 */

const mutations = {
  // Called from user/init once per app load/sign in
  // Also called from auth/handleSignInSuccess for upgrading anonymous accounts
  setUidEmail(state, user) {
    const { email = '', uid, isAnonymous = false } = user;
    state.uid = uid;
    state.email = email;
    state.isAnonymous = isAnonymous;
  },

  setUserData(state, { isMember, memberLevel, marketingOptOut, numDaysOpenedApp }) {
    // state.createdAt = createdAt.toDate();
    state.isMember = isMember;
    state.memberLevel = memberLevel;
    state.marketingOptOut = marketingOptOut || false;
    state.numDaysOpenedApp = numDaysOpenedApp || 0;
    state.userDataLoaded = true;
  },

  setMembership(state, { isMember, memberLevel }) {
    state.isMember = isMember;
    state.memberLevel = memberLevel;
  },

  setWelcomeSlideNum(state, num) {
    state.welcomeSlideNum = num;
  },

  clearUser(state) {
    Object.assign(state, initialState());
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
